import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  UserCheck,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  Building,
  Mail,
  Phone,
  MapPin,
  Calendar,
  FileText,
  Download,
  AlertTriangle
} from 'lucide-react';
import api from '../../services/api.js';

const SellerApproval = () => {
  const [pendingSellers, setPendingSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionType, setActionType] = useState(''); // 'approve' or 'reject'

  useEffect(() => {
    fetchPendingSellers();
  }, []);

  const fetchPendingSellers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/sellers/pending');
      if (response.data.success) {
        setPendingSellers(response.data.data.sellers || response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching pending sellers:', error);
      setPendingSellers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (sellerId) => {
    try {
      await api.patch(`/admin/sellers/${sellerId}/approve`);
      fetchPendingSellers(); // Refresh the list
      setShowModal(false);
    } catch (error) {
      console.error('Error approving seller:', error);
    }
  };

  const handleReject = async (sellerId) => {
    try {
      await api.patch(`/admin/sellers/${sellerId}/reject`, {
        rejectionReason
      });
      fetchPendingSellers(); // Refresh the list
      setShowModal(false);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting seller:', error);
    }
  };

  const openActionModal = (seller, action) => {
    setSelectedSeller(seller);
    setActionType(action);
    setShowModal(true);
  };

  const confirmAction = () => {
    if (actionType === 'approve') {
      handleApprove(selectedSeller._id);
    } else if (actionType === 'reject') {
      handleReject(selectedSeller._id);
    }
  };

  const SellerCard = ({ seller }) => (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
            <span className="text-lg font-semibold text-primary-600">
              {seller.fullName?.charAt(0)?.toUpperCase()}
            </span>
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-semibold text-gray-900">{seller.fullName}</h3>
            <p className="text-sm text-gray-500">ID: {seller._id.slice(-8)}</p>
          </div>
        </div>
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      </div>

      {/* Business Info */}
      <div className="mb-4">
        <div className="flex items-center mb-2">
          <Building className="w-4 h-4 text-gray-400 mr-2" />
          <span className="text-sm font-medium text-gray-900">{seller.businessName}</span>
        </div>
        <div className="text-sm text-gray-600 ml-6">
          {seller.businessType} • Est. {seller.yearEstablished}
        </div>
      </div>

      {/* Contact Info */}
      <div className="mb-4 space-y-2">
        <div className="flex items-center text-sm text-gray-600">
          <Mail className="w-4 h-4 text-gray-400 mr-2" />
          {seller.email}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <Phone className="w-4 h-4 text-gray-400 mr-2" />
          {seller.phone}
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <MapPin className="w-4 h-4 text-gray-400 mr-2" />
          {seller.registeredAddress?.city}, {seller.registeredAddress?.state}
        </div>
      </div>

      {/* Registration Date */}
      <div className="mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
          Applied on {new Date(seller.createdAt).toLocaleDateString()}
        </div>
      </div>

      {/* Documents */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Documents</h4>
        <div className="grid grid-cols-2 gap-2">
          {seller.documents?.gstCertificate && (
            <a
              href={seller.documents.gstCertificate}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-xs text-blue-600 hover:text-blue-800"
            >
              <FileText className="w-3 h-3 mr-1" />
              GST Certificate
            </a>
          )}
          {seller.documents?.panCard && (
            <a
              href={seller.documents.panCard}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-xs text-blue-600 hover:text-blue-800"
            >
              <FileText className="w-3 h-3 mr-1" />
              PAN Card
            </a>
          )}
          {seller.documents?.aadharCard && (
            <a
              href={seller.documents.aadharCard}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-xs text-blue-600 hover:text-blue-800"
            >
              <FileText className="w-3 h-3 mr-1" />
              Aadhar Card
            </a>
          )}
          {seller.documents?.cancelledCheque && (
            <a
              href={seller.documents.cancelledCheque}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-xs text-blue-600 hover:text-blue-800"
            >
              <FileText className="w-3 h-3 mr-1" />
              Cancelled Cheque
            </a>
          )}
        </div>
      </div>

      {/* Categories */}
      <div className="mb-4">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Categories</h4>
        <div className="flex flex-wrap gap-1">
          {seller.selectedCategories?.map((category, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
            >
              {category.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </span>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="flex space-x-3 pt-4 border-t border-gray-200">
        <Link
          to={`/admin/sellers/${seller._id}`}
          className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <Eye className="w-4 h-4 mr-2" />
          View Details
        </Link>
        <button
          onClick={() => openActionModal(seller, 'approve')}
          className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          Approve
        </button>
        <button
          onClick={() => openActionModal(seller, 'reject')}
          className="flex-1 inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
        >
          <XCircle className="w-4 h-4 mr-2" />
          Reject
        </button>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <UserCheck className="w-8 h-8 mr-3 text-primary-600" />
              Seller Approval
            </h1>
            <p className="text-gray-600 mt-1">Review and approve pending seller applications</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-primary-600">{pendingSellers.length}</div>
            <div className="text-sm text-gray-500">Pending Applications</div>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pending sellers...</p>
        </div>
      ) : pendingSellers.length === 0 ? (
        <div className="text-center py-12">
          <UserCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Applications</h3>
          <p className="text-gray-600">All seller applications have been reviewed.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {pendingSellers.map((seller) => (
            <SellerCard key={seller._id} seller={seller} />
          ))}
        </div>
      )}

      {/* Action Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center mb-4">
                {actionType === 'approve' ? (
                  <CheckCircle className="w-6 h-6 text-green-600 mr-3" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-red-600 mr-3" />
                )}
                <h3 className="text-lg font-medium text-gray-900">
                  {actionType === 'approve' ? 'Approve Seller' : 'Reject Seller'}
                </h3>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  Are you sure you want to {actionType} <strong>{selectedSeller?.fullName}</strong>?
                </p>
              </div>

              {actionType === 'reject' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rejection Reason *
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-primary-500"
                    placeholder="Please provide a reason for rejection..."
                    required
                  />
                </div>
              )}

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmAction}
                  disabled={actionType === 'reject' && !rejectionReason.trim()}
                  className={`flex-1 px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                    actionType === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {actionType === 'approve' ? 'Approve' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerApproval;
